/*
 * Copyright Deskshare Inc.
 * All rights reserved.
 *
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import Cookies from "js-cookie";
import * as Sentry from "@sentry/react";
import { apiClient } from "./ApiClient";
import { DEFAULT_TIMEOUT } from "./cache";
import { configuration } from "@/lib/configuration";
import { authClient } from "@/lib/auth-client";
import type {
  FormApiResponse,
  PricingData,
  ShoppingCartStatus,
  CameraRequestData,
  ContactUsData,
  LostLicenseData,
  MailProductLinkData,
  ReviewSubmissionData,
  ProductSupportData,
} from "./types";

/**
 * Normalize a phone number to E.164 format (e.g., +919898989998).
 * react-phone-input-2 stores digits only without the leading '+'.
 * Django's phonenumbers.parse() requires E.164 — validation fails without it.
 */
const toE164 = (phone: string): string => {
  if (!phone) return phone;
  const t = phone.trim();
  return t.startsWith("+") ? t : `+${t.replace(/\D/g, "")}`;
};

export const api = {
  // Product data (i18n endpoints)
  products: {
    getAll: () => apiClient.get<Record<string, unknown>>("product/"),
    getBySlug: <T = unknown>(slug: string) =>
      apiClient.get<T>(`product/${slug}/`),
    getHeader: <T = unknown>() => apiClient.get<T>("product-header/"),
    getHomepage: <T = unknown>() => apiClient.get<T>("homepage/"),
    getDownload: <T = unknown>() => apiClient.get<T>("download/"),
    getAboutUs: <T = unknown>() => apiClient.get<T>("about-us/"),
    getAboutUsMobile: <T = unknown>() => apiClient.get<T>("about-us-mobile/"),
    getVideoTutorials: <T = unknown>() =>
      apiClient.get<T>("video-tutorial-manual/"),
    getArticles: <T = unknown>() => apiClient.get<T>("allarticles/"),
    getArticleBySlug: <T = unknown>(slug: string) =>
      apiClient.get<T>(`allarticles/${slug}/`),
    // Product subresource endpoints (slug-scoped, i18n)
    getVersions: <T = unknown>(slug: string) =>
      apiClient.get<T>(`product/${slug}/version/`),
    getReviews: <T = unknown>(slug: string) =>
      apiClient.get<T>(`product/${slug}/review/`),
    getCart: <T = unknown>(slug: string) =>
      apiClient.get<T>(`product/${slug}/cart/`),
    getUserManual: <T = unknown>(slug: string) =>
      apiClient.get<T>(`product/${slug}/user_manual/`),
  },

  // Form submissions (non-i18n endpoints)
  forms: {
    submitCameraRequest: async (
      data: CameraRequestData,
    ): Promise<FormApiResponse> => {
      const response = await apiClient.postDirect<{ message: string }>(
        "api/camera-request-form/",
        {
          first_name: data.firstName,
          last_name: data.lastName,
          phone_number: data.phone ?? "",
          email: data.email,
          camera_brand: data.cameraBrand,
          camera_model: data.cameraModel,
          ipaddress: data.cameraIP,
          port_number: data.portNumber,
          camera_username: data.username ?? "",
          camera_password: data.password ?? "",
          camera_instruct: data.instruction ?? "",
        },
      );
      return { message: response.message, status: 200 };
    },

    submitContactUs: async (data: ContactUsData): Promise<FormApiResponse> => {
      const response = await apiClient.postDirect<{ message: string }>(
        "api/contactus-form/",
        {
          first_name: data.firstName,
          last_name: data.lastName,
          email: data.email,
          message: data.message,
          phone_number: data.contactNumber ?? "",
          language: data.language ?? "en", // Forwarded for i18n routing and analytics
          // B2: "date" field removed — the back-end required it but immediately
          // discarded it in favour of server-generated time. Sending a value that
          // is never read is wasteful and confusing; removed from both sides.
        },
      );
      return { message: response.message, status: 200 };
    },

    submitLostLicense: async (
      data: LostLicenseData,
    ): Promise<FormApiResponse> => {
      const response = await apiClient.postDirect<{ message: string }>(
        "api/lost-license-form/",
        {
          first_name: data.firstName,
          last_name: data.lastName,
          email: data.email,
          product_name: data.application,
          approx_date_purchase: data.formattedDate,
          // S-2: react-phone-input-2 stores digits without '+' (e.g., "919898989998").
          // Django phonenumbers.parse() requires E.164 format (+919898989998).
          phone_number: toE164(data.phone ?? ""),
          // DD-6: Forward deduplication token so the backend can reject duplicate taps.
          // Without these fields, request.data.get('submissionId') returns None and the
          // 5-second server-side dedup cache is bypassed entirely on every request.
          ...(data.submissionId ? { submissionId: data.submissionId } : {}),
          ...(data.clientTimestamp
            ? { clientTimestamp: data.clientTimestamp }
            : {}),
        },
      );
      return { message: response.message, status: 200 };
    },

    submitProductSupport: async (
      data: ProductSupportData,
    ): Promise<FormApiResponse> => {
      const formData = new FormData();
      formData.append("productHelpInquiry", data.productHelpInquiry);
      formData.append("type", data.type);
      formData.append("message", data.message);
      formData.append("email", data.email);
      if (data.product) formData.append("product", data.product);
      if (data.image) formData.append("image", data.image, "screenshot.png");
      if (data.accountOrderId)
        formData.append("accountOrderId", data.accountOrderId);
      if (data.company) formData.append("company", data.company);

      const response = await apiClient.postFormData<{ message: string }>(
        "api/support-form/",
        formData,
      );
      return { message: response.message, status: 200 };
    },

    sendProductLink: async (
      data: MailProductLinkData,
    ): Promise<FormApiResponse> => {
      const response = await apiClient.postDirect<{ message: string }>(
        "api/send_product_link/",
        {
          email: data.email,
          product_link: data.downloadProduct,
        },
      );
      return { message: response.message, status: 200 };
    },

    // V-1 fix: Route website reviews to the FMS at the correct REST endpoint.
    // The previous path (/api/feedback/submit-review) no longer exists on the
    // FMS; the canonical route is /api/feedback/review/ (noun resource,
    // trailing slash per Django convention).
    //
    // Field changes from the FCR:
    //   reviewer_name  → user_name       (FMS serializer field)
    //   review / review_title → comment  (title folded in so nothing is lost)
    //   language       → app_language    (FMS serializer field)
    //   product_slug   → sent in both product_id and product_slug fields;
    //                    FMS resolves the product from whichever it reads
    //
    // A-1: Trailing slash is explicit — Django 301-redirects POST without it,
    //      which may drop the request body in some browsers.
    // A-2: AbortController gives the call a 15s timeout; without it a stalled
    //      FMS leaves the form spinner running indefinitely.
    // A-6: Sentry captures any review submission failure; the raw fetch bypasses
    //      the shared ApiClient that would otherwise report errors automatically.
    submitReview: async (
      data: ReviewSubmissionData,
    ): Promise<FormApiResponse> => {
      const base = configuration.feedbackAPIURL;
      if (!base) throw new Error("Review submission is not configured.");

      // Fold the review title into the comment body so it is never silently
      // dropped — the FMS intake uses a single `comment` field.
      const reviewTitle = data.reviewTitle.trim();
      const reviewBody = data.review.trim();
      const comment = reviewTitle
        ? `Title: ${reviewTitle}\n\n${reviewBody}`
        : reviewBody;

      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 15000);

      try {
        const response = await fetch(`${base}/api/feedback/review/`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            // Send slug in both fields; FMS reads whichever it expects.
            product_id: data.productSlug,
            product_slug: data.productSlug,
            product_version: "web",
            user_name: data.reviewerName,
            email: data.email,
            rating: data.rating,
            comment,
            order_number: data.orderNumber ?? "",
            app_language: data.language ?? "en",
            operating_system: "Web",
          }),
          signal: controller.signal,
        });

        const parsed = await response.json().catch(() => ({}));
        if (!response.ok || parsed.success === false) {
          const err = new Error(parsed.message || `HTTP ${response.status}`);
          // A-6: Capture failures — raw fetch bypasses the shared ApiClient.
          Sentry.captureException(err, {
            tags: { api_method: "POST (review)" },
            contexts: {
              request: { url: `${base}/api/feedback/review/`, method: "POST" },
              response: { status: response.status },
            },
            extra: { status: response.status },
          });
          const statusErr = err as Error & { status?: number };
          statusErr.status = response.status;
          throw statusErr;
        }
        return { message: parsed.message, status: response.status };
      } catch (err) {
        if (err instanceof Error && err.name === "AbortError") {
          const timeoutErr = new Error("Review submission timed out. Please try again.");
          Sentry.captureException(timeoutErr, {
            tags: { api_method: "POST (review)", timeout: true },
          });
          throw timeoutErr;
        }
        throw err;
      } finally {
        clearTimeout(timer);
      }
    },
  },

  // Support (uninstall feedback and other app-level submissions)
  support: {
    // DD-04: submitUninstallFeedback moved from raw fetch to apiClient.postDirect —
    // consistent with all other form submissions; gets retry/timeout from ApiClient.
    submitUninstallFeedback: async (
      data: Record<string, string>,
    ): Promise<void> => {
      await apiClient.postDirect<{ message: string }>(
        "api/uninstall-feedback/",
        data,
      );
    },
  },

  // Pricing (external DeskShare API)
  // AF-2: Timer wrapped in try/finally. This function will be removed entirely
  // when R-2 Part B (backend currency_code/currency_symbol migration) is deployed.
  pricing: {
    getProductPrice: async (productId: string): Promise<PricingData | null> => {
      // AF-14: String(null) → "null", String(0) → "0" — both truthy but invalid IDs.
      if (!productId || productId === "null" || productId === "0") return null;

      const url = `${import.meta.env.VITE_REACT_APP_API_DESKSHARE_URL}/general/get-product-price/`;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), DEFAULT_TIMEOUT);

      try {
        const response = await fetch(url, {
          headers: { id: productId },
          signal: controller.signal,
        });

        if (!response.ok) {
          const err = new Error(`Pricing API error: ${response.status}`);
          Sentry.captureException(err, {
            tags: { api_method: "GET (pricing)" },
            contexts: { request: { url, method: "GET" } },
          });
          throw err;
        }

        return (await response.json()) as PricingData;
      } finally {
        clearTimeout(timeoutId); // always clears — previously leaked on network errors
      }
    },
  },

  // Support forum (external forum API)
  forum: {
    getData: async <T>(path: string): Promise<T | null> => {
      const url = `${import.meta.env.VITE_REACT_APP_SUPPORT_FORUM_URL}${path}`;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), DEFAULT_TIMEOUT);
      try {
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) {
          Sentry.captureMessage(
            `Forum API error: ${response.status} for ${path}`,
            "warning",
          );
          return null;
        }
        return (await response.json()) as T;
      } catch (error) {
        Sentry.captureException(
          error instanceof Error ? error : new Error(String(error)),
          {
            tags: { api_method: "GET (forum)" },
            contexts: { request: { url, method: "GET" } },
          },
        );
        return null;
      } finally {
        clearTimeout(timeoutId); // always clears — prevents timer leak when NodeBB is unreachable
      }
    },
  },

  // Authentication & cart status
  auth: {
    getShoppingCartLoginStatus: (): ShoppingCartStatus => {
      // Shopping-cart login writes browser cookies (DeskShareUserCookie /
      // __Secure-session_token). Check those first so cart-login users keep
      // working exactly as before.
      const hasCartCookie = Object.keys(Cookies.get()).some(
        (key) =>
          key.startsWith("DeskShareUserCookie") ||
          key.startsWith("__Secure-session_token"),
      );

      // S-LOGIN: The website's own sign-in flow (allauth headless) stores its
      // session token in localStorage (ds_session_token), not a cookie — so
      // hasCartCookie is false for website-login users even when they have a
      // valid session. Combine both sources so the header shows "My Account"
      // regardless of which login path the visitor used.
      //
      // authClient.isAuthenticated() reads ds_session_token from localStorage;
      // it is already used throughout the app. No new source is introduced.
      //
      // DD-5: woocommerce_test_custom cookie removed — stale WooCommerce
      // integration cookie, no longer set by any active Deskshare flow.
      const isuser_loggedin = hasCartCookie || authClient.isAuthenticated();
      const is_user = isuser_loggedin;

      return { isuser_loggedin, is_user };
    },
  },

  // Generic product data endpoints (V-3: namespace was missing, causing TS2339)
  data: {
    /** Fetch product-specific data by path (e.g., "comparison/", "features/").
     *  Returns null on error — callers should handle gracefully. */
    fetch: async <T = unknown>(path: string): Promise<T | null> => {
      try {
        return await apiClient.get<T>(`product/${path}`);
      } catch {
        return null;
      }
    },
    /** Fetch common (non-product-scoped) data by path.
     *  Returns undefined on error — distinguishes from null product data. */
    fetchCommon: async <T = unknown>(path: string): Promise<T | undefined> => {
      try {
        return await apiClient.get<T>(path);
      } catch {
        return undefined;
      }
    },
  },

  // Cache management
  cache: {
    clear: () => apiClient.clearCache(),
    clearPath: (path: string) => apiClient.clearCacheForPath(path),
  },
};
